













import { Component } from 'vue-property-decorator'
import { Chart } from 'highcharts-vue'
import { BaseQuestion } from '@/components/Survey/QuestionTypes/BaseQuestion'
import Tab from '@/components/Tabs/Tab.vue'
import Tabs from '@/components/Tabs/Tabs.vue'
import { getOptionsTextFromQuestion } from '@/components/Survey/Utilities/SurveyUtilities'
import {
  getCategoriesWithExtremityLabels,
  generateChartSubtitle,
  generateMatrixSeries,
  getMatrixTotals,
  getSegmentCount,
  SegmentTotalsRecord
} from '@/components/Survey/Utilities/ChartUtilities'
import { TabData } from '@/components/Tabs/TabTypes'
import SliceNotSupported from '@/components/Survey/SliceNotSupported.vue'
import { slicedChartTooltipFormatter } from '../Utilities/ChartTooltipUtilities'

@Component({
  components: {
    Highcharts: Chart,
    Tabs,
    Tab,
    SliceNotSupported
  }
})
export default class MatrixQuestion extends BaseQuestion {
  DEFAULT_MAX_RATING = 5
  DEFAULT_MIN_RATING = 1

  get chartHasData (): boolean {
    if (this.isSliced || this.isVariant) {
      return getSegmentCount(this.typedSlicedResponses[this.selectedTab]) > 0
    }
    return this.indexResponses.length > 0
  }

  get sortedCategories () {
    return Object.keys(this.matrixIndexTotals)
  }

  get matrixIndexTotals () : SegmentTotalsRecord {
    return getMatrixTotals(
      this.categories,
      this.sortKey,
      this.scaleSize,
      this.indexResponses.map(response => response.answer),
      this.question.hasNaOpt
    )
  }

  get segmentTotals () : SegmentTotalsRecord {
    return getMatrixTotals(
      this.categories,
      this.sortKey,
      this.scaleSize,
      this.typedSlicedResponses[this.selectedTab].map(response => response.answer),
      this.question.hasNaOpt
    )
  }

  get slicedSeries () {
    const sortedTotals: SegmentTotalsRecord = {}
    this.sortedCategories.forEach(category => {
      sortedTotals[category] = this.segmentTotals[category]
    })

    return generateMatrixSeries(
      sortedTotals,
      this.matrixScaleCategories,
      this.matrixDisplayCategories,
      { hasNaOpt: this.question.hasNaOpt, colors: this.colorConfiguration.divergentPalette }
    ).slice().reverse()
  }

  get indexSeries () {
    return generateMatrixSeries(
      this.matrixIndexTotals,
      this.matrixScaleCategories,
      this.matrixDisplayCategories,
      { hasNaOpt: this.question.hasNaOpt, colors: this.colorConfiguration.divergentPalette }
    ).slice().reverse()
  }

  get series () {
    if (this.isSliced || this.isVariant) {
      return this.slicedSeries
    } else {
      return this.indexSeries
    }
  }

  get chartOptions () {
    return {
      ...this.defaultChartOptions(),
      xAxis: {
        ...this.defaultChartOptions().xAxis,
        categories: this.sortedCategories
      },
      exporting: {
        ...this.defaultChartOptions().exporting,
        chartOptions: {
          ...this.defaultChartOptions().exporting.chartOptions,
          subtitle: {
            text: this.chartSubtitle
          }
        }
      },
      series: this.series,
      tooltip: (this.isSliced || this.isVariant)
        ? slicedChartTooltipFormatter(this.segmentTotals, this.question.type, this.sliceByQuestion?.type)
        : slicedChartTooltipFormatter(this.matrixIndexTotals, this.question.type, this.sliceByQuestion?.type)
    }
  }

  get categories () {
    return getOptionsTextFromQuestion(this.question)
  }

  get sortKey () {
    if (this.question.scaleProperties?.maxRating) {
      return this.question.scaleProperties!.maxRating
    } else {
      return this.DEFAULT_MAX_RATING
    }
  }

  get scaleSize () {
    let maxRating = this.DEFAULT_MAX_RATING
    let minRating = this.DEFAULT_MIN_RATING

    if (this.question.scaleProperties!.maxRating) {
      maxRating = this.question.scaleProperties!.maxRating
    }

    if (this.question.scaleProperties!.minRating) {
      minRating = this.question.scaleProperties!.minRating
    }

    return (maxRating - minRating) + 1
  }

  get matrixDisplayCategories () {
    return getCategoriesWithExtremityLabels(this.matrixScaleCategories, this.question.scaleProperties!)
  }

  get matrixScaleCategories () {
    const categories = []
    for (let i = 1; i <= this.scaleSize; i++) {
      categories.push(i.toString())
    }

    return categories
  }

  get chartSubtitle () {
    if (this.isSliced || this.isVariant) {
      const nSize = getSegmentCount(this.typedSlicedResponses[this.selectedTab])
      return generateChartSubtitle(nSize, this.sliceBy, this.selectedTab)
    }
    return ''
  }

  get tabData (): TabData[] {
    return Object.entries(this.typedSlicedResponses).map(([key, responses]) => {
      return {
        title: key,
        segmentCount: getSegmentCount(responses)
      }
    })
  }
}
